import React, { useEffect, useState, useRef } from 'react';
import { XIcon, ChevronDownIcon, CheckIcon, EditIcon, CheckCircleIcon } from 'lucide-react';
import { trackSignUp, trackLogin } from '../../lib/analytics/events';
interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function AuthModal({
  isOpen,
  onClose
}: AuthModalProps) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [countryCode, setCountryCode] = useState('+966');
  const [showOTP, setShowOTP] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpInputs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (showOTP && resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
  }, [showOTP, resendTimer]);
  useEffect(() => {
    if (!isOpen) {
      setShowOTP(false);
      setShowSuccess(false);
      setOtp(['', '', '', '', '', '']);
      setResendTimer(60);
      setCanResend(false);
    }
  }, [isOpen]);
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };
  const handleContinue = () => {
    setShowOTP(true);
    setResendTimer(60);
    setCanResend(false);
  };
  const handleVerifyOTP = () => {
    setShowSuccess(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  const handleResendOTP = (method: 'whatsapp' | 'sms') => {
    setResendTimer(60);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    otpInputs.current[0]?.focus();
  };
  const handleEditPhone = () => {
    setShowOTP(false);
    setOtp(['', '', '', '', '', '']);
  };
  if (!isOpen) return null;
  if (showSuccess) {
    return <div className="fixed inset-0 z-[200] flex items-center justify-center" data-id="element-3865" data-mp="3eu05c2kfcmbw">
        <div className="absolute inset-0 bg-black bg-opacity-70" onClick={onClose} data-id="element-3866" data-mp="3tjftdaskmdnw"></div>
        <div className="relative w-[95%] max-w-sm bg-white rounded-lg shadow-lg overflow-hidden mx-auto z-[201]" data-id="element-3867" data-mp="3oo0gom6aunt2">
          <div className="p-8 text-center" data-id="element-3868" data-mp="2epspkwk3q2c">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3869" data-mp="2fr27kxxc4hy">
              <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-3870" data-mp="28869gzp3v4k7" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3871" data-mp="247e086g8gznx">Welcome!</h2>
            <p className="text-gray-600" data-id="element-3872" data-mp="1u1vx8a8gr5m1">You have successfully logged in</p>
          </div>
        </div>
      </div>;
  }
  if (showOTP) {
    return <div className="fixed inset-0 z-[200] flex items-center justify-center" data-id="element-3873" data-mp="1y5tusgi548cz">
        <div className="absolute inset-0 bg-black bg-opacity-70" onClick={onClose} data-id="element-3874" data-mp="1kbt50c961vor"></div>
        <div className="relative w-[95%] max-w-md bg-white rounded-lg shadow-lg overflow-hidden mx-auto z-[201]" data-id="element-3875" data-mp="1p9b5de94b2l5">
          <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 p-1 z-10" data-id="element-3876" data-mp="1fd84sctwzc0b">
            <XIcon className="w-5 h-5" data-id="element-3877" data-mp="11erygmzyrnip" />
          </button>
          <div className="p-6" data-id="element-3878" data-mp="16c9xnlo6iodi">
            <h2 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-3879" data-mp="16dl5pxonqiaf">
              Verify your mobile number
            </h2>
            <p className="text-sm text-gray-600 text-center mb-2" data-id="element-3880" data-mp="2vs9c9iakh60j">
              Enter the 6-digit OTP sent to
            </p>
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-3881" data-mp="10obc45dxfdzt">
              <span className="text-base font-medium text-gray-900" data-id="element-3882" data-mp="10m8pzwfwa50d">
                +966 {phoneNumber}
              </span>
              <button onClick={handleEditPhone} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-3883" data-mp="qq5c5tbokhxl">
                <EditIcon className="w-4 h-4 text-[#1E2B55]" data-id="element-3884" data-mp="mo0k2z0vrtd2" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-1 mb-6" data-id="element-3885" data-mp="hr1rwm0s1whi">
              <span className="text-sm text-gray-600" data-id="element-3886" data-mp="cxphbfsxnq7r">via</span>
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24" data-id="element-3887" data-mp="7znq27lcskl7">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3888" data-mp="325kmr6jonhy" />
              </svg>
              <span className="text-sm font-medium text-green-600" data-id="element-3889" data-mp="187c9e8y4wbeq">
                WhatsApp
              </span>
            </div>
            <div className="flex justify-center gap-2 mb-6" data-id="element-3890" data-mp="2bhgnnyusj25j">
              {otp.map((digit, index) => <input key={index} ref={el => otpInputs.current[index] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-[#1E2B55] focus:outline-none transition-colors" data-id="element-3891" data-mp="26j5elqcbkvod" data-mpi={index} />)}
            </div>
            <div className="text-center mb-6" data-id="element-3892" data-mp="1wjxrwcjp3ool">
              <p className="text-sm text-gray-600 mb-3" data-id="element-3893" data-mp="3czh47312t58c">
                Didn't receive the OTP?
              </p>
              {canResend ? <div className="flex gap-3 justify-center" data-id="element-3894" data-mp="3804hcia7fy5r">
                  <button onClick={() => handleResendOTP('whatsapp')} className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors text-sm" data-id="element-3895" data-mp="3gh53tjsch5rf">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-3896" data-mp="36llddcwjuza2">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3897" data-mp="33a66pk5e51j" />
                    </svg>
                    WhatsApp
                  </button>
                  <button onClick={() => handleResendOTP('sms')} className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors text-sm" data-id="element-3898" data-mp="2wvrpcosmr5h0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-3899" data-mp="1gf5j1himr6d4">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" data-id="element-3900" data-mp="xipc65ptp3j" />
                    </svg>
                    SMS
                  </button>
                </div> : <p className="text-sm text-gray-500" data-id="element-3901" data-mp="jp1sqe3nj563">
                  Resend OTP in{' '}
                  <span className="font-bold text-[#1E2B55]" data-id="element-3902" data-mp="omtib1gtqf74">
                    {resendTimer}s
                  </span>
                </p>}
            </div>
            <button onClick={handleVerifyOTP} disabled={otp.some(digit => !digit)} className="w-full py-2.5 bg-[#1E2B55] text-white rounded-md hover:bg-[#162041] transition-colors mb-4 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3903" data-mp="2qeq73cs05lju">
              Confirm
            </button>
            <p className="text-xs text-gray-500 text-center" data-id="element-3904" data-mp="3usx3g4uqxnhr">
              By continuing, you agree to share your contact details with Innova
              for service purposes and accept their{' '}
              <a href="#" className="text-[#1E2B55] underline" data-id="element-3905" data-mp="8lp5hnm5oivr">
                terms
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#1E2B55] underline" data-id="element-3906" data-mp="cm8z4h7it4d5">
                policies
              </a>
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="fixed inset-0 z-[200] flex items-center justify-center" data-id="element-3907" data-mp="mdn4v6q2f0eq">
      <div className="absolute inset-0 bg-black bg-opacity-70" onClick={onClose} data-id="element-3908" data-mp="rc6srco7ym9q"></div>
      <div className="relative w-[95%] max-w-sm bg-white rounded-lg shadow-lg overflow-hidden mx-auto z-[201]" data-id="element-3909" data-mp="wb1kj445zl5z">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 p-1" data-id="element-3910" data-mp="2gtvbvlpicl8i">
          <XIcon className="w-5 h-5" data-id="element-3911" data-mp="2bso43mnd7m1u" />
        </button>
        <div className="p-5" data-id="element-3912" data-mp="26umiov2gvan7">
          <div className="mb-5" data-id="element-3913" data-mp="26vxof0fh2sgy">
            <h2 className="text-xl font-semibold text-gray-800" data-id="element-3914" data-mp="26x8sz2gqs494">
              Sign in / Register
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-3915" data-mp="2zjtrcmvmxgm6">
              Enter your number to continue
            </p>
          </div>
          <div className="mb-4" data-id="element-3916" data-mp="2ukgvxdqp717z">
            <div className="flex border border-gray-300 rounded-md overflow-hidden" data-id="element-3917" data-mp="2ulij2gkzne62">
              <div className="relative inline-block" data-id="element-3918" data-mp="2pspblky4xrqa">
                <button className="flex items-center justify-between px-2 py-2.5 bg-gray-50 border-r border-gray-300 min-w-[80px]" data-id="element-3919" data-mp="2kv71hr8dt6gp">
                  <div className="flex items-center" data-id="element-3920" data-mp="3ibcs0n26sil1">
                    <span className="mr-1 text-green-600" data-id="element-3921" data-mp="3djl3sj6i6oy2">🇸🇦</span>
                    <span data-id="element-3922" data-mp="3dia0ekgyzj7h">{countryCode}</span>
                  </div>
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 ml-1" data-id="element-3923" data-mp="1bn3uijv5zogi" />
                </button>
              </div>
              <input type="tel" className="flex-1 px-3 py-2.5 focus:outline-none w-full text-sm" placeholder="e.g: 501918660" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} data-id="element-3924" data-mp="1gkm66i0kghs7" />
            </div>
          </div>
          <div className="mb-4 border border-gray-200 rounded-md p-3 bg-gray-50" data-id="element-3925" data-mp="sxno5fwst1s6">
            <div className="flex items-center justify-between" data-id="element-3926" data-mp="wyheefxpqso8">
              <div className="flex items-center" data-id="element-3927" data-mp="iy6k12x72vyb">
                <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-2" data-id="element-3928" data-mp="izq474lz1r5d"></div>
                <span className="text-xs text-gray-600" data-id="element-3929" data-mp="2dbo42fye5aol">Verifying...</span>
              </div>
              <img src="https://cloudflare.com/favicon.ico" alt="CloudFlare" className="h-4" data-id="element-3930" data-mp="2ia93id5tjqbz" />
            </div>
          </div>
          <button onClick={handleContinue} className="w-full py-2.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors mb-4 text-sm" data-id="element-3931" data-mp="1o2qqbyx09qz">
            Continue
          </button>
          <div className="flex items-center" data-id="element-3932" data-mp="blyzix4dqslx">
            <input type="checkbox" id="keepLoggedIn" checked={keepLoggedIn} onChange={() => setKeepLoggedIn(!keepLoggedIn)} className="hidden" data-id="element-3933" data-mp="bkxkkhk8tlyx" />
            <button onClick={() => setKeepLoggedIn(!keepLoggedIn)} className={`w-4 h-4 flex-shrink-0 flex items-center justify-center border rounded mr-2 ${keepLoggedIn ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-3934" data-mp="zbt8v4phpz1f">
              {keepLoggedIn && <CheckIcon className="w-3 h-3 text-white" data-id="element-3935" data-mp="31511ffu46d59" />}
            </button>
            <label htmlFor="keepLoggedIn" className="text-xs text-gray-700 cursor-pointer" onClick={() => setKeepLoggedIn(!keepLoggedIn)} data-id="element-3936" data-mp="2w5xu1oqnvuu4">
              Keep me logged in
            </label>
          </div>
        </div>
      </div>
    </div>;
}